import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Delete, MapPin, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { masterApi, api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ZapCartLogo } from "@/components/ZapCartLogo";
import { queuePasskeyOffer } from "@/lib/passkey";

export default function StaffSelector() {
  const { master, enterPin, setActiveStaff, logout } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (master?.needs_pin_setup) navigate("/setup-pin", { replace: true }); }, [master, navigate]);
  const [staff, setStaff] = useState(null);
  const [pinFor, setPinFor] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const loadStaff = () => { setStaff(null); masterApi.get("/staff").then((r) => setStaff(r.data)).catch((e) => { setStaff([]); toast.error(errMsg(e)); }); };
  useEffect(() => { loadStaff(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // PIN is verified server-side only; role comes from the server response.
  const activate = async (s, enteredPin) => {
    setBusy(true);
    try {
      const { data } = await masterApi.post("/auth/pin", { role: "staff", pin: enteredPin, name: s.name });
      enterPin(data.token, data.user);
      setActiveStaff({ name: s.name, color: s.color });
      queuePasskeyOffer();
      toast.success(`Signed in as ${s.name}`);
      const back = sessionStorage.getItem("zb_return_to"); sessionStorage.removeItem("zb_return_to");
      navigate(back || "/dashboard", { replace: true });
    } catch (e) { toast.error(errMsg(e)); setPin(""); setBusy(false); }
  };

  const onDigit = (d) => {
    if (busy || pin.length >= 4) return;
    const nx = pin + d; setPin(nx);
    if (nx.length === 4) activate(pinFor, nx);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-5">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-5">
          <ZapCartLogo size={48} />
          <p data-no-translate className="mt-2 text-slate-900 font-extrabold text-lg font-head">{master?.shop_name}</p>
          <p className="flex items-center gap-1 text-slate-400 text-xs"><MapPin className="w-3 h-3" /> Who's using ZapBasket?</p>
        </div>

        {!pinFor ? (
          <div className="grid grid-cols-2 gap-3" data-testid="staff-profile-list">
            {staff === null && [0, 1].map((i) => <div key={i} data-testid="staff-loading" className="h-[132px] rounded-2xl bg-slate-100 animate-pulse" />)}
            {staff?.length === 0 && (
              <button data-testid="staff-retry" onClick={loadStaff} className="col-span-2 text-sm font-bold text-blue-600 py-6 border border-dashed border-slate-300 rounded-2xl">Couldn't load profiles — tap to retry</button>
            )}
            {(staff || []).map((s) => (
              <button key={s.id} data-testid={`staff-profile-${s.name}`}
                onClick={() => { setPin(""); setPinFor(s); }}
                className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col items-center gap-2 shadow-sm active:scale-95 transition-all">
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-extrabold" style={{ background: s.color }}>
                  {s.name[0]}
                </div>
                <span data-no-translate className="font-bold text-slate-800 text-sm">{s.name}</span>
                <span className="text-[10px] text-slate-400">🔒 Enter PIN</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-xl">
            <p data-no-translate className="text-center font-bold text-slate-800 mb-1">{pinFor.name}</p>
            <p className="text-center text-slate-400 text-xs mb-4">{busy ? "Signing in…" : "Enter 4-digit PIN"}</p>
            <div className="flex justify-center gap-3 mb-5">
              {[0, 1, 2, 3].map((i) => (showPin && pin[i]
                ? <span key={i} className="w-4 h-4 flex items-center justify-center font-mono font-bold text-emerald-600">{pin[i]}</span>
                : <div key={i} className={`w-4 h-4 rounded-full ${pin.length > i ? "bg-emerald-500 scale-110" : "bg-slate-200"}`} />))}
              <button data-testid="staff-pin-toggle" aria-label={showPin ? "Hide PIN" : "Show PIN"} onClick={() => setShowPin((s) => !s)} className="ml-1 text-slate-400 hover:text-slate-700">{showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button key={n} data-testid={`staff-pin-${n}`} onClick={() => onDigit(String(n))} className="h-14 rounded-2xl bg-slate-50 border border-slate-200 text-slate-900 text-2xl font-semibold font-mono active:scale-95 active:bg-emerald-50">{n}</button>
              ))}
              <button data-testid="staff-pin-back" disabled={busy} onClick={() => { setPinFor(null); setPin(""); }} className="h-14 rounded-2xl text-slate-400 text-xs">Back</button>
              <button data-testid="staff-pin-0" onClick={() => onDigit("0")} className="h-14 rounded-2xl bg-slate-50 border border-slate-200 text-slate-900 text-2xl font-semibold font-mono active:scale-95 active:bg-emerald-50">0</button>
              <button data-testid="staff-pin-delete" disabled={busy} onClick={() => setPin(pin.slice(0, -1))} className="h-14 rounded-2xl bg-slate-50 border border-slate-200 text-slate-400 flex items-center justify-center active:scale-95"><Delete className="w-6 h-6" /></button>
            </div>
          </div>
        )}

        <button data-testid="staff-logout" onClick={() => { logout(); navigate("/auth"); }} className="w-full flex items-center justify-center gap-1 text-slate-400 hover:text-slate-600 text-sm mt-6"><LogOut className="w-4 h-4" /> Sign out of store account</button>
      </motion.div>
    </div>
  );
}
